"use client";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import FullLogoPrimary from "@/assets/logos/full-logo-secondary.png";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { TermsOfUseMessages } from "@/messages/terms/terms.messages";
import Footer from "@/mui/Footer";

import { TermsSection } from "./components/TermsSection";

type TermsProps = {
  messages: TermsOfUseMessages;
};

export default function Terms({ messages }: TermsProps) {
  const {
    title,
    termDescription1,
    termDescription2,
    acceptanceTitle,
    acceptanceDescription,
    changesTitle,
    changesDescription1,
    changesDescription2,
    changesDescription3,
    partnersTermsOfUseTitle,
    partnersTermsOfUseDescription,
    privacyTitle,
    privacyDescription1,
    privacyDescription2,
    privacyDescription3,
    privacyDescription4,
    privacyDescription5,
    communicationPreferencesTitle,
    communicationPreferencesDescription,
    intellectualPropertyTitle,
    intellectualPropertyDescription,
    contractingAndPayingServicesTitle,
    contractingAndPayingServicesDescription1,
    contractingAndPayingServicesDescription2,
    contractingAndPayingServicesDescription3,
    contractingAndPayingServicesDescription4,
    contractingAndPayingServicesDescription5,
    contractingAndPayingServicesDescription6,
    contractingAndPayingServicesDescription7,
    supportTitle,
    supportDescription1,
    supportDescription2,
    supportDescription3,
    maintenanceAndSlaLevelsTitle,
    maintenanceAndSlaLevelsDescription1,
    maintenanceAndSlaLevelsDescription2,
    maintenanceAndSlaLevelsDescription3,
    maintenanceAndSlaLevelsDescription4,
    maintenanceAndSlaLevelsDescription5,
    maintenanceAndSlaLevelsDescription6,
    maintenanceAndSlaLevelsDescription7,
    maintenanceAndSlaLevelsDescription8,
    acceptableUsesTitle,
    acceptableUsesSubTitle1,
    acceptableUsesDescription1,
    acceptableUsesSubTitle2,
    acceptableUsesDescription2,
    acceptableUsesDescription3,
    acceptableUsesDescription4,
    acceptableUsesDescription5,
    acceptableUsesDescription6,
    acceptableUsesDescription7,
    acceptableUsesDescription8,
    acceptableUsesDescription9,
    acceptableUsesDescription10,
    acceptableUsesDescription11,
    acceptableUsesDescription12,
    acceptableUsesDescription13,
    accountManagementTitle,
    accountManagementDescription1,
    accountManagementDescription2,
    accountManagementDescription3,
    accountManagementDescription4,
    accountManagementDescription5,
    accountManagementDescription6,
    financialServicesTitle,
    financialServicesDescription,
    suspensionOfServiceTitle,
    suspensionOfServiceDescription1,
    suspensionOfServiceDescription2,
    disclaimersTitle,
    disclaimersSubtitle1,
    disclaimersDescription1,
    disclaimersSubtitle2,
    disclaimersDescription2,
    termAndTerminationTitle,
    termAndTerminationDescription1,
    termAndTerminationDescription2,
    termAndTerminationDescription3,
    termAndTerminationDescription4,
    termAndTerminationDescription5,
    termAndTerminationDescription6,
    termAndTerminationDescription7,
    termAndTerminationDescription8,
    termAndTerminationDescription9,
    termAndTerminationDescription10,
    termAndTerminationDescription11,
    termAndTerminationDescription12,
    additionalProvisionsTitle,
    additionalProvisionsDescription1,
    additionalProvisionsDescription2,
    additionalProvisionsDescription3,
    additionalProvisionsDescription4,
    additionalProvisionsDescription5,
    additionalProvisionsDescription6,
  } = messages;

  const logoWidth = 220;
  const logoHeight = 64;

  return (
    <Grid container justifyContent="center" alignItems="center" height="100%">
      <Grid size={8}>
        <MDBox my={6}>
          <Card>
            <MDBox p={4} maxWidth="1000px" mx="auto">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                py={4}
              >
                <img
                  src={FullLogoPrimary}
                  width={logoWidth}
                  height={logoHeight}
                  alt="Talk2Buy logo"
                />

                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  textTransform="uppercase"
                  sx={{ textAlign: "center" }}
                  mt={5}
                >
                  {title}
                </MDTypography>
              </MDBox>

              <TermsSection allItemsAsText description={[termDescription1, termDescription2]} />
              <TermsSection title={acceptanceTitle} description={acceptanceDescription} />
              <TermsSection
                title={changesTitle}
                allItemsAsText
                description={[changesDescription1, changesDescription2, changesDescription3]}
              />
              <TermsSection
                title={partnersTermsOfUseTitle}
                description={partnersTermsOfUseDescription}
              />
              <TermsSection
                allItemsAsText
                title={privacyTitle}
                description={[privacyDescription1, privacyDescription2]}
              />
              <TermsSection description={[privacyDescription3, privacyDescription4]} />
              <TermsSection allItemsAsText description={privacyDescription5} />
              <TermsSection
                title={communicationPreferencesTitle}
                description={communicationPreferencesDescription}
              />
              <TermsSection
                title={intellectualPropertyTitle}
                description={intellectualPropertyDescription}
              />
              <TermsSection
                title={contractingAndPayingServicesTitle}
                allItemsAsText
                description={[
                  contractingAndPayingServicesDescription1,
                  contractingAndPayingServicesDescription2,
                  contractingAndPayingServicesDescription3,
                  contractingAndPayingServicesDescription4,
                  contractingAndPayingServicesDescription5,
                  contractingAndPayingServicesDescription6,
                  contractingAndPayingServicesDescription7,
                ]}
              />

              <TermsSection
                title={supportTitle}
                allItemsAsText
                description={[supportDescription1, supportDescription2, supportDescription3]}
              />
              <TermsSection
                title={maintenanceAndSlaLevelsTitle}
                allItemsAsText
                description={[
                  maintenanceAndSlaLevelsDescription1,
                  maintenanceAndSlaLevelsDescription2,
                  maintenanceAndSlaLevelsDescription3,
                  maintenanceAndSlaLevelsDescription4,
                  maintenanceAndSlaLevelsDescription5,
                  maintenanceAndSlaLevelsDescription6,
                  maintenanceAndSlaLevelsDescription7,
                  maintenanceAndSlaLevelsDescription8,
                ]}
              />
              <TermsSection
                title={acceptableUsesTitle}
                allItemsAsText
                description={[
                  acceptableUsesSubTitle1,
                  acceptableUsesDescription1,
                  acceptableUsesSubTitle2,
                  acceptableUsesDescription2,
                ]}
              />
              <TermsSection
                firstItemAsText
                description={[
                  acceptableUsesDescription3,
                  acceptableUsesDescription4,
                  acceptableUsesDescription5,
                  acceptableUsesDescription6,
                  acceptableUsesDescription7,
                  acceptableUsesDescription8,
                  acceptableUsesDescription9,
                  acceptableUsesDescription10,
                  acceptableUsesDescription11,
                  acceptableUsesDescription12,
                  acceptableUsesDescription13,
                ]}
              />
              <TermsSection
                title={accountManagementTitle}
                allItemsAsText
                description={[
                  accountManagementDescription1,
                  accountManagementDescription2,
                  accountManagementDescription3,
                  accountManagementDescription4,
                  accountManagementDescription5,
                  accountManagementDescription6,
                ]}
              />
              <TermsSection
                title={financialServicesTitle}
                description={financialServicesDescription}
              />
              <TermsSection
                title={suspensionOfServiceTitle}
                allItemsAsText
                description={[suspensionOfServiceDescription1, suspensionOfServiceDescription2]}
              />
              <TermsSection
                title={disclaimersTitle}
                allItemsAsText
                description={[
                  disclaimersSubtitle1,
                  disclaimersDescription1,
                  disclaimersSubtitle2,
                  disclaimersDescription2,
                ]}
              />
              <TermsSection
                title={termAndTerminationTitle}
                allItemsAsText
                description={[
                  termAndTerminationDescription1,
                  termAndTerminationDescription2,
                  termAndTerminationDescription3,
                  termAndTerminationDescription4,
                  termAndTerminationDescription5,
                  termAndTerminationDescription6,
                  termAndTerminationDescription7,
                  termAndTerminationDescription8,
                  termAndTerminationDescription9,
                  termAndTerminationDescription10,
                  termAndTerminationDescription11,
                  termAndTerminationDescription12,
                ]}
              />
              <TermsSection
                title={additionalProvisionsTitle}
                allItemsAsText
                description={[
                  additionalProvisionsDescription1,
                  additionalProvisionsDescription2,
                  additionalProvisionsDescription3,
                  additionalProvisionsDescription4,
                  additionalProvisionsDescription5,
                  additionalProvisionsDescription6,
                ]}
              />
            </MDBox>
          </Card>
          <Footer messages={messages} />
        </MDBox>
      </Grid>
    </Grid>
  );
}
